import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { LOCALSTORAGE_USER_CACHE_PREFIX, TEMPLATE_ID } from "constants/index";
import config from "config";
import type User from "entities/User.entity";
import type Organization from "entities/Organization.entity";
import type IOrg from "models/Organization.model";
import type { CreateWorkspaceArgs } from "models/LocalStorage.model";
import agent from "server";
import { generateRandomString } from "utils/strings";
import { isFollowingInvitationLink } from "utils/invitations";
import { getPostLoginRedirect } from "utils/redirects";
import posthog from "posthog-js";

async function resumeCachedWorkspaceCreation(activeOrgId: string) {
  const storageKey = `${LOCALSTORAGE_USER_CACHE_PREFIX}-create-workspace`;
  const createWorkspaceBody = window.localStorage.getItem(storageKey);
  window.localStorage.removeItem(storageKey);
  if (!createWorkspaceBody) {
    return null;
  }

  const { name, gitRepo, orgId, workspaceClassId, setupScript } = JSON.parse(
    createWorkspaceBody,
  ) as CreateWorkspaceArgs;
  const targetOrgId = orgId || activeOrgId;
  const workspaceVersion: "v1" | "v0" = "v1";
  const args = {
    name,
    cloudCredId: config.cloudCredID,
    workspaceTemplateId: TEMPLATE_ID,
    workspaceClassId,
    gitRepo,
    workspaceVersion,
    isStoppable: false,
    ...(setupScript ? { setupScript } : {}),
  };
  const response = await agent.Workspaces.create(targetOrgId, args);

  return {
    targetOrgId,
    workspaceId: response.data?.id,
  };
}

export function useOrganizationSetup({
  enabled,
  isOrganizationsLoading,
  hasOrganizationData,
  isOrganizationError,
  organizationError,
  organizations,
  initialActiveOrgId,
  user,
  createOrSyncOrg,
}: {
  enabled: boolean;
  isOrganizationsLoading: boolean;
  hasOrganizationData: boolean;
  isOrganizationError: boolean;
  organizationError: unknown;
  organizations: Organization[];
  initialActiveOrgId: string;
  user: User | null;
  createOrSyncOrg: (name: string) => Promise<IOrg>;
}): boolean {
  const history = useHistory();
  const setupUserIdRef = useRef<string>();
  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);

  useEffect(() => {
    setupUserIdRef.current = undefined;
    setHasCompletedSetup(false);
  }, [enabled, user?.id]);

  useEffect(() => {
    const userId = user?.id;
    if (
      !enabled ||
      !userId ||
      isOrganizationsLoading ||
      (!hasOrganizationData && !isOrganizationError) ||
      setupUserIdRef.current === userId
    ) {
      return;
    }

    setupUserIdRef.current = userId;

    const setupOrganizations = async () => {
      try {
        if (isOrganizationError) {
          const message =
            organizationError instanceof Error
              ? organizationError.message
              : "Failed to load organizations.";
          console.error("[orgs] failed to load organizations", {
            userId,
            email: user.email,
            message,
          });
          posthog.capture("OrgLoadError", {
            userId,
            email: user.email,
            message,
          });
          history.push("/error/500", {
            title: "Error Loading in Orgs",
            description: `${message} Error loading in orgs. Try refreshing the page!`,
          });
          return;
        }

        let setupActiveOrgId = initialActiveOrgId;
        const isProcessingInvite = isFollowingInvitationLink(
          window.location.pathname + window.location.search,
          getPostLoginRedirect(),
        );

        if (organizations.length === 0 && !isProcessingInvite) {
          const orgName = `${user.username}-${generateRandomString(4)}`;
          const createdOrganization = await createOrSyncOrg(orgName);
          setupActiveOrgId = createdOrganization.id;
        }

        const resumedWorkspace =
          await resumeCachedWorkspaceCreation(setupActiveOrgId);
        if (resumedWorkspace) {
          history.push(`/org/${resumedWorkspace.targetOrgId}/environments`, {
            highlightedWorkspaceId: resumedWorkspace.workspaceId,
          });
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load organizations.";
        history.push("/error/500", {
          title: "Error Loading in Orgs",
          description: `${message} Error loading in orgs. Try refreshing the page!`,
        });
      } finally {
        if (setupUserIdRef.current === userId) {
          setHasCompletedSetup(true);
        }
      }
    };

    void setupOrganizations();
  }, [
    createOrSyncOrg,
    enabled,
    hasOrganizationData,
    history,
    initialActiveOrgId,
    isOrganizationError,
    isOrganizationsLoading,
    organizationError,
    organizations,
    user,
  ]);

  return hasCompletedSetup;
}
